import { CameraOutlined, EditOutlined } from "@ant-design/icons"
import { Button, Form, Input, Modal, Switch } from "antd"
import React, { useState } from "react"
import { configs } from "~/configs"
import { t } from "~/languages/i18n"
import { FieldType, MediaData, PostFormProps } from "~/types/types"
import { isVideoFile } from "~/utils/helper"
import { slugPattern } from "~/utils/validationPatterns"
import CKEditorComponent from "../../../lib/Ckeditor"
import { replaceImageUrls } from "../../../lib/utils"
import AddNewMedia from "../../media/pages/AddNewMedia"
import Media from "../../media/pages/Media"
import { FaqEditor, FaqItem } from "../../parent-question/components"
import { checkSlug } from "../services/post"

const removeAccents = (str: string) => {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[đĐ]/g, "d")
    .replace(/[âÂ]/g, "a")
    .replace(/[êÊ]/g, "e")
    .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
    .replace(/\s+/g, "-")
    .toLowerCase()
}

const isValidJson = (str: string): boolean => {
  try {
    JSON.parse(str)
    return true
  } catch (e) {
    return false
  }
}

const PostForm: React.FC<PostFormProps> = ({
  form,
  onFinishFailed,
  quillValue,
  onFinish,
  selectedFaqs,
  onChangeFaqs,
  faqEnabled = false,
  onFaqEnabledChange,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChooseMedia, setIsChooseMedia] = useState(true)
  const [selectedMetaImage, setSelectedMetaImage] = useState<string | null>(null)
  const [modalMetaImage, setModalMetaImage] = useState(false)
  const [isEditableSlug, setIsEditableSlug] = useState(true)
  const [editorInsertFn, setEditorInsertFn] = useState<((html: string) => void) | null>(null)
  const [saveSelectionFn, setSaveSelectionFn] = useState<(() => void) | null>(null)
  const [internalFaqs, setInternalFaqs] = useState<FaqItem[]>(selectedFaqs || [])

  const faqs = selectedFaqs !== undefined ? selectedFaqs : internalFaqs
  const setFaqs = selectedFaqs !== undefined ? onChangeFaqs! : setInternalFaqs

  const handleSubmit = () => {
    const formData = form.getFieldsValue()
    onFinish(formData)
  }

  const handleOpenModal = (modalMetaImage: boolean) => {
    // Save editor selection before opening modal
    if (saveSelectionFn) {
      saveSelectionFn()
    }
    setModalMetaImage(modalMetaImage)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
  }

  const handleSelectMedia = (media: MediaData) => {
    const mediaUrl = `${configs.API_URL}${media.url}`
    let html: string

    if (isVideoFile(media)) {
      html = `<p>&nbsp;</p><video src="${mediaUrl}" controls style="width:100%;max-width:800px;"></video><p>&nbsp;</p>`
    } else {
      html = `<img src="${mediaUrl}" alt="" />`
    }

    if (editorInsertFn) {
      editorInsertFn(html)
    } else {
      form.setFieldsValue({ content: `${form.getFieldValue("content") || ""}${html}` })
    }

    setIsModalOpen(false)
  }

  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = removeAccents(nameValue)

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response = await checkSlug(slug)
        return response.data.length === 0
      } catch {
        return false
      }
    }

    const isUnique = await isSlugUnique(slugValue)
    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }

  const handleFaqEnabledChange = (checked: boolean) => {
    if (onFaqEnabledChange) {
      onFaqEnabledChange(checked)
    }
  }

  const handleFaqsChange = (newFaqs: FaqItem[]) => {
    setFaqs(newFaqs)
  }

  return (
    <Form
      form={form}
      className="flex flex-col w-[77%]"
      name="basic"
      initialValues={{ remember: true }}
      onFinish={handleSubmit}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
    >
      <Form.Item<FieldType>
        name="name"
        rules={[{ required: true, message: t("Please enter article name!") }]}
        className="mt-4"
      >
        <Input placeholder={t("Add Name")} className="text-xl custom-input" onBlur={handleNameBlur} />
      </Form.Item>
      <div className="flex w-full">
        <Form.Item<FieldType>
          className="w-1/2 mr-4"
          name="slug"
          rules={[
            {
              pattern: slugPattern,
              message: 'Slug must match the following: "A-Z, a-z, 0-9, - ,_ ,. ,~"',
            },
          ]}
          label={<span className="text-2xl font-medium pb-2">{t("Slug")}</span>}
          extra={<span className="text-sm">{t("May not need to be entered (automatically render by name)")}</span>}
        >
          <Input disabled={isEditableSlug} placeholder={t("Slug")} className="custom-input" />
        </Form.Item>
        <Button
          className="self-center mb-2"
          icon={<EditOutlined />}
          color={!isEditableSlug ? "primary" : "default"}
          variant="outlined"
          onClick={() => setIsEditableSlug(!isEditableSlug)}
        >
          {t("Edit")}
        </Button>
      </div>
      <Form.Item>
        <Button icon={<CameraOutlined />} onClick={() => handleOpenModal(false)}>
          {t("Add media")}
        </Button>
      </Form.Item>

      <Form.Item<FieldType> name="content">
        <div
          style={{
            height: "500px",
            overflowY: "auto",
            position: "relative",
          }}
        >
          <CKEditorComponent
            value={replaceImageUrls(form.getFieldValue("content") || "")}
            onChange={(data: string) => {
              const updatedContent = replaceImageUrls(data)
              form.setFieldsValue({ content: updatedContent })
            }}
            onInsertMedia={(callback, saveSelection) => {
              setEditorInsertFn(() => callback)
              setSaveSelectionFn(() => saveSelection)
            }}
          />
        </div>
      </Form.Item>

      <Form.Item<FieldType> name="excerpt" label={<span className="text-base font-medium">{t("Excerpt")}</span>}>
        <Input className="custom-input" />
      </Form.Item>

      <div className="flex items-center gap-3 mt-4 mb-4 p-3 border border-gray-200 rounded-lg bg-gray-50">
        <Switch checked={faqEnabled} onChange={handleFaqEnabledChange} />
        <span className="text-base font-medium">{t("Enable FAQs")}</span>
      </div>

      {faqEnabled && (
        <FaqEditor faqs={faqs as FaqItem[]} onChange={handleFaqsChange as (faqs: FaqItem[]) => void} />
      )}

      <span className="text-base font-medium pt-2 pb-2">{t("SEO")}</span>

      <div className="grid grid-cols-2 gap-x-5 gap-y-2 p-5 border rounded-[10px]">
        <Form.Item<FieldType>
          name="metaTitle"
          label={<span className="text-base font-medium p">{t("Meta Title")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="keywords"
          label={<span className="text-base font-medium">{t("Meta Keywords")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaDescription"
          label={<span className="text-base font-medium">{t("Meta Description")}</span>}
        >
          <Input.TextArea className="custom-input py-2" rows={5} />
        </Form.Item>

        <Form.Item<FieldType>
          name="structuredData"
          label={<span className="text-base font-medium">{t("Structured Data")}</span>}
          rules={[
            {
              validator: (_, value) => {
                if (value && !isValidJson(value)) {
                  return Promise.reject(new Error(t("Invalid JSON format!")))
                }
                return Promise.resolve()
              },
            },
          ]}
        >
          <Input.TextArea className="custom-input py-2" rows={5} placeholder={t('@example: {"id": "string"}')} />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaRobots"
          label={<span className="text-base font-medium">{t("Meta Robots")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>

        <Form.Item<FieldType>
          name="metaViewport"
          label={<span className="text-base font-medium">{t("Meta Viewport")}</span>}
        >
          <Input className="custom-input py-2" />
        </Form.Item>
      </div>

      <Modal
        open={isModalOpen}
        title={<span className="ml-4">{t("Chọn ảnh")}</span>}
        onCancel={handleCloseModal}
        style={{ top: 20 }}
        width="95%"
        footer={null}
      >
        <div className="ml-4 mt-5">
          <Button
            onClick={() => setIsChooseMedia(true)}
            className="mr-2"
            style={{ backgroundColor: isChooseMedia ? "blue" : "initial", color: isChooseMedia ? "white" : "initial" }}
          >
            {t("Select Media")}
          </Button>
          <Button
            onClick={() => setIsChooseMedia(false)}
            style={{
              backgroundColor: !isChooseMedia ? "blue" : "initial",
              color: !isChooseMedia ? "white" : "initial",
            }}
          >
            {t("Upload Media")}
          </Button>
        </div>
        <div>
          {isChooseMedia ? (
            <Media
              isOpenModal={true}
              onSelectMedia={media => {
                modalMetaImage ? handleSelectMedia(media) : handleSelectMedia(media)
              }}
            />
          ) : (
            <AddNewMedia isOpenModal={true} setChooseMedia={setIsChooseMedia} />
          )}
        </div>
      </Modal>
    </Form>
  )
}

export default PostForm
